import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Select,
  Option,
  Textarea,
  Typography,
  IconButton,
  Alert,
} from '@mui/joy';
import { ArrowLeft as ArrowBack, Play as PlayArrow } from '@phosphor-icons/react';
import { ChecklistsAPI } from '../../lib/apiChecklists';
import { api } from '../../lib/api';
import type { Checklist, CreateChecklistRunRequest } from '../../types/checklists';
import type { Job, Opportunity } from '../../types';

const ChecklistRunPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    job_id: '',
    opportunity_id: '',
    association_type: 'job' as 'job' | 'opportunity',
    notes: '',
  });

  const load = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await ChecklistsAPI.retrieve(parseInt(id));
      setChecklist(data);
    } catch (error) {
      console.error('Failed to load checklist:', error);
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const loadJobsAndOpportunities = async () => {
    setDataLoading(true);
    try {
      const [jobsResponse, oppsResponse] = await Promise.all([
        api.getJobs(),
        api.getOpportunities()
      ]);
      setJobs(jobsResponse.data || []);
      setOpportunities(oppsResponse.data || []);
    } catch (error) {
      console.error('Failed to load jobs and opportunities:', error);
      setError('Failed to load jobs and opportunities');
    } finally {
      setDataLoading(false);
    }
  };

  const handleStartRun = async () => {
    if (!checklist) return;

    if (!formData.job_id && !formData.opportunity_id) {
      setError('Please select either a Job or Opportunity');
      return;
    }

    // Validate that the selected job/opportunity exists
    if (formData.association_type === 'job' && formData.job_id) {
      const selectedJob = jobs.find(j => j.id.toString() === formData.job_id);
      if (!selectedJob) {
        setError(`Selected job (ID: ${formData.job_id}) does not exist. Please refresh the page and try again.`);
        return;
      }
    } else if (formData.association_type === 'opportunity' && formData.opportunity_id) {
      const selectedOpportunity = opportunities.find(o => o.id.toString() === formData.opportunity_id);
      if (!selectedOpportunity) {
        setError(`Selected opportunity (ID: ${formData.opportunity_id}) does not exist. Please refresh the page and try again.`);
        return;
      }
    }

    setStarting(true);
    setError(null);
    
    try {
      const payload: CreateChecklistRunRequest = {
        checklist: checklist.id,
        ...(formData.notes.trim() && { notes: formData.notes.trim() }),
      };

      if (formData.association_type === 'job' && formData.job_id) {
        payload.job = parseInt(formData.job_id);
      } else if (formData.association_type === 'opportunity' && formData.opportunity_id) {
        payload.opportunity = parseInt(formData.opportunity_id);
      }

      // Debug logging
      console.log('Creating checklist run with payload:', payload);
      console.log('Available jobs:', jobs.map(j => ({ id: j.id, label: j.job_number || j.address_1 })));
      console.log('Available opportunities:', opportunities.map(o => ({ id: o.id, label: o.name })));

      const run = await ChecklistsAPI.createRun(payload);
      
      // Navigate to the run execution page (we'll implement this later)
      navigate(`/checklists/runs/${run.id}`);
    } catch (error) {
      console.error('Failed to start checklist run:', error);
      
      // Try to parse backend error messages
      let errorMessage = 'Failed to start checklist run';
      if (error instanceof Error) {
        if (error.message.includes('400')) {
          errorMessage = 'Invalid request. Please check that the selected job/opportunity exists and try again.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Permission denied. You may not have access to this job/opportunity.';
        } else if (error.message.includes('404')) {
          errorMessage = 'The checklist or selected job/opportunity was not found.';
        } else {
          errorMessage = `Failed to start checklist run: ${error.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    load();
    loadJobsAndOpportunities();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography level="body-sm">Loading...</Typography>
      </Box>
    );
  }

  if (!checklist) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography level="body-sm">Checklist not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/checklists')} variant="plain">
          <ArrowBack />
        </IconButton>
        <Typography level="h3">Start Checklist Run</Typography>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography level="title-md" sx={{ mb: 2 }}>{checklist.title}</Typography>
        
        {checklist.description && (
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            {checklist.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography level="body-sm">
            <strong>{checklist.steps.length}</strong> steps
          </Typography>
          {checklist.steps.filter(s => s.photos_required).length > 0 && (
            <Typography level="body-sm" sx={{ color: 'warning.main' }}>
              <strong>{checklist.steps.filter(s => s.photos_required).length}</strong> require photos
            </Typography>
          )}
        </Box>

        <Typography level="title-sm" sx={{ mt: 2, mb: 1 }}>Steps Preview:</Typography>
        <Box sx={{ pl: 2 }}>
          {checklist.steps.map((step, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography level="body-sm">
                {index + 1}. {step.title}
                {step.photos_required && (
                  <Typography component="span" sx={{ color: 'warning.main', ml: 1 }}>
                    📸
                  </Typography>
                )}
                {step.allow_skip && (
                  <Typography component="span" sx={{ color: 'info.main', ml: 1 }}>
                    (skippable)
                  </Typography>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography level="title-md" sx={{ mb: 2 }}>Associate with Job or Opportunity</Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl>
            <FormLabel>Association Type</FormLabel>
            <Select
              value={formData.association_type}
              onChange={(_, value) => {
                setFormData({
                  ...formData,
                  association_type: value as 'job' | 'opportunity',
                  job_id: '',
                  opportunity_id: '',
                });
              }}
            >
              <Option value="job">Job</Option>
              <Option value="opportunity">Opportunity</Option>
            </Select>
          </FormControl>

          {formData.association_type === 'job' ? (
            <FormControl required>
              <FormLabel>Select Job</FormLabel>
              <Select
                value={formData.job_id}
                onChange={(_, value) => setFormData({ ...formData, job_id: value || '' })}
                placeholder={dataLoading ? "Loading jobs..." : "Choose a job..."}
                disabled={dataLoading}
              >
                {dataLoading ? (
                  <Option value="" disabled>Loading jobs...</Option>
                ) : jobs.length === 0 ? (
                  <Option value="" disabled>No jobs available</Option>
                ) : (
                  jobs.map((job) => (
                    <Option key={job.id} value={job.id.toString()}>
                      {job.job_number || job.address_1 || `Job #${job.id}`}
                    </Option>
                  ))
                )}
              </Select>
            </FormControl>
          ) : (
            <FormControl required>
              <FormLabel>Select Opportunity</FormLabel>
              <Select
                value={formData.opportunity_id}
                onChange={(_, value) => setFormData({ ...formData, opportunity_id: value || '' })}
                placeholder={dataLoading ? "Loading opportunities..." : "Choose an opportunity..."}
                disabled={dataLoading}
              >
                {dataLoading ? (
                  <Option value="" disabled>Loading opportunities...</Option>
                ) : opportunities.length === 0 ? (
                  <Option value="" disabled>No opportunities available</Option>
                ) : (
                  opportunities.map((opp) => (
                    <Option key={opp.id} value={opp.id.toString()}>
                      {opp.name || `Opportunity #${opp.id}`}
                    </Option>
                  ))
                )}
              </Select>
            </FormControl>
          )}

          <FormControl>
            <FormLabel>Notes (Optional)</FormLabel>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Enter any notes about this checklist run..."
              minRows={3}
            />
          </FormControl>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          onClick={handleStartRun}
          loading={starting}
          startDecorator={<PlayArrow />}
          disabled={
            dataLoading ||
            (formData.association_type === 'job' && !formData.job_id) ||
            (formData.association_type === 'opportunity' && !formData.opportunity_id)
          }
        >
          Start Checklist Run
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/checklists')}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ChecklistRunPage;